import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment-timezone';
import { v4 } from 'uuid';
import { useTranslation } from 'react-i18next';

import EventDateRangeInput, {
  TypeEvent
} from './EventDateRangeInput/EventDateRangeInput';
import {
  EVENT_ESTIMATED_FIELD_DEFAULTS,
  getEventEnd
} from 'v1/helpers/byModel/EventHelper';
import {
  eventFieldsFromDateRange,
  parseMixedCalendarDay
} from 'v1/helpers/mixedDateBoundary';

/** First allowed chunk whose start is strictly after `afterEnd` (or the first chunk if none). */
export function findNextAllowedRangeChunk(allowedRanges, afterEnd) {
  if (!allowedRanges?.length) {
    return null;
  }
  const afterDay = afterEnd
    ? moment(parseMixedCalendarDay(afterEnd)).startOf('day')
    : null;
  return (
    [...allowedRanges]
      .sort((a, b) => a.from.getTime() - b.from.getTime())
      .find(
        ({ from }) =>
          !afterDay || moment(from).startOf('day').isAfter(afterDay, 'day')
      ) ?? null
  );
}

function buildEventForAdd({ data, allowedRanges, eventType }) {
  const last = data.at(-1) ?? null;
  const date_type = last?.date_type ?? 'RECURRING_DEFAULT';
  const nextChunk = findNextAllowedRangeChunk(
    allowedRanges,
    last ? getEventEnd(last) : null
  );

  const base = {
    id: `temp-${v4()}`,
    date_type,
    event_type: eventType
  };

  if (nextChunk) {
    return {
      ...base,
      ...eventFieldsFromDateRange(nextChunk.from, nextChunk.to),
      estimated_start_time:
        last?.estimated_start_time ??
        EVENT_ESTIMATED_FIELD_DEFAULTS.estimated_start_time,
      estimated_end_time:
        last?.estimated_end_time ??
        EVENT_ESTIMATED_FIELD_DEFAULTS.estimated_end_time,
      estimated_daily_hours:
        last?.estimated_daily_hours ??
        EVENT_ESTIMATED_FIELD_DEFAULTS.estimated_daily_hours,
      estimated_daily_minutes:
        last?.estimated_daily_minutes ??
        EVENT_ESTIMATED_FIELD_DEFAULTS.estimated_daily_minutes
    };
  }

  const nextDay = last
    ? moment(parseMixedCalendarDay(getEventEnd(last)))
        .add(1, 'd')
        .toDate()
    : new Date();

  return {
    ...base,
    ...eventFieldsFromDateRange(nextDay, nextDay),
    ...EVENT_ESTIMATED_FIELD_DEFAULTS
  };
}

// TODO: would be good to review these sizes & bring them in line with our other components
const EventDateRange = ({
  className,
  isReadOnly,
  appearance = 'silent',
  size = 'L',
  data,
  onChange,
  eventType,
  labelStart,
  labelEnd,
  limits,
  filterDate,
  allowedRanges,
  allowEmpty = true,
  allowMultiple = true,
  highlightDates,
  hideTimePicker = false
}) => {
  // useEffect(_ => {
  //   Array.isArray(data) &&
  //     onChange &&
  //     onChange(data.map(date => ({ id: date.id || `temp-${v4()}`, ...date })));
  // }, []);

  const { t } = useTranslation('v1_shared');

  const effectiveLabelStart =
    labelStart || t('EventDateRange.labelStart', { defaultValue: 'Starts' });
  const effectiveLabelEnd =
    labelEnd || t('EventDateRange.labelEnd', { defaultValue: 'Ends' });

  const handleAdd = _ => {
    onChange([...data, buildEventForAdd({ data, allowedRanges, eventType })]);
  };

  const handleChange = event => {
    Array.isArray(data)
      ? onChange(data.map(d => (d.id === event.id ? event : d)))
      : onChange(event);
  };

  const handleRemove = id => {
    !isReadOnly && onChange(data.filter(d => d.id !== id));
  };

  const allowRemove = () => {
    if (!allowEmpty && data.length <= 1) {
      return false;
    }
    return true;
  };

  const renderRow = event => (
    <EventDateRangeInput
      key={event && event.id}
      event={event}
      size={size}
      appearance={appearance}
      isReadOnly={isReadOnly}
      onChange={value => handleChange(value)}
      onRemove={
        Array.isArray(data) && allowRemove()
          ? () => handleRemove(event.id)
          : undefined
      }
      labelStart={effectiveLabelStart}
      labelEnd={effectiveLabelEnd}
      limits={limits}
      filterDate={filterDate}
      allowedRanges={allowedRanges}
      highlightDates={highlightDates}
      hideTimePicker={hideTimePicker}
    />
  );

  return (
    <div className={classnames(['EventDateRange', className])}>
      {Array.isArray(data) ? (
        <>
          {data.map(event => renderRow(event))}
          {!isReadOnly && allowMultiple && (
            <span
              className="EventDateRange-addAnotherBtn btn-small-link link-with-icon"
              onClick={handleAdd}
            >
              <img src="/images/icon_add_gray.svg" alt="" />{' '}
              {t('EventDateRange.addAnotherDate', {
                defaultValue: 'Add another date'
              })}
            </span>
          )}
        </>
      ) : (
        renderRow(data)
      )}
    </div>
  );
};

EventDateRange.propTypes = {
  className: PropTypes.string,
  isReadOnly: PropTypes.bool,
  size: PropTypes.oneOf(['XS', 'S', 'M']),
  eventType: PropTypes.string, // 'event_type' - E.g. 'PRODUCTION'
  data: PropTypes.oneOfType([
    // Pass object for single, array for multiple
    TypeEvent,
    PropTypes.arrayOf(TypeEvent)
  ]),
  labelStart: PropTypes.string,
  labelEnd: PropTypes.string,
  onChange: PropTypes.func,
  limits: PropTypes.shape({
    minDate: PropTypes.object,
    maxDate: PropTypes.object
  }),
  allowEmpty: PropTypes.any,
  allowMultiple: PropTypes.bool,
  filterDate: PropTypes.func,
  allowedRanges: PropTypes.arrayOf(
    PropTypes.shape({
      from: PropTypes.instanceOf(Date).isRequired,
      to: PropTypes.instanceOf(Date).isRequired
    })
  ),
  highlightDates: PropTypes.array,
  hideTimePicker: PropTypes.bool
};

export default EventDateRange;
